import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { AppButton, AppTextField } from "../../../components/form";
import { PageSectionCard } from "../../../components/layout";
import {
  platformAdminService,
  type PlatformOpensearchTemplate,
  type PlatformOpensearchTemplatePayload,
} from "../../../services/platformAdminService";

type FormState = {
  serviceUrl: string;
  adminUsername: string;
  adminPassword: string;
  sslInsecure: boolean;
  enabled: boolean;
  filterAdByBackendRoles: boolean;
};

const emptyForm = (): FormState => ({
  serviceUrl: "",
  adminUsername: "admin",
  adminPassword: "",
  sslInsecure: true,
  enabled: true,
  filterAdByBackendRoles: true,
});

function toForm(tpl: PlatformOpensearchTemplate): FormState {
  return {
    serviceUrl: tpl.serviceUrl || "",
    adminUsername: tpl.adminUsername || "admin",
    adminPassword: "",
    sslInsecure: tpl.sslInsecure !== false,
    enabled: tpl.enabled !== false,
    filterAdByBackendRoles: tpl.filterAdByBackendRoles !== false,
  };
}

export default function DatasourcesPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [configured, setConfigured] = useState(false);
  const [passwordConfigured, setPasswordConfigured] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tpl = await platformAdminService.getBeasOpensearchTemplate();
      setForm(toForm(tpl));
      setConfigured(!!tpl.configured);
      setPasswordConfigured(!!tpl.passwordConfigured);
    } catch {
      setError(t("superAdmin.datasources.loadError"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const payload = (): PlatformOpensearchTemplatePayload => ({
    serviceUrl: form.serviceUrl.trim(),
    adminUsername: form.adminUsername.trim(),
    adminPassword: form.adminPassword.trim() || undefined,
    sslInsecure: form.sslInsecure,
    enabled: form.enabled,
    filterAdByBackendRoles: form.filterAdByBackendRoles,
  });

  const onSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const tpl = await platformAdminService.upsertBeasOpensearchTemplate(payload());
      setForm(toForm(tpl));
      setConfigured(!!tpl.configured);
      setPasswordConfigured(!!tpl.passwordConfigured);
      setSuccess(t("superAdmin.datasources.saveSuccess"));
    } catch {
      setError(t("superAdmin.datasources.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setError(null);
    setSuccess(null);
    try {
      await platformAdminService.testBeasOpensearchTemplate(payload());
      setSuccess(t("superAdmin.datasources.testSuccess"));
    } catch {
      setError(t("superAdmin.datasources.testError"));
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" py={4}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  return (
    <PageSectionCard
      title={t("superAdmin.datasources.title")}
      description={t("superAdmin.datasources.description")}
    >
      <Stack spacing={2} maxWidth={560}>
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
        {!configured && (
          <Alert severity="warning">{t("superAdmin.datasources.notConfigured")}</Alert>
        )}
        <AppTextField
          label={t("superAdmin.datasources.serviceUrl")}
          value={form.serviceUrl}
          onChange={(e) => setForm((f) => ({ ...f, serviceUrl: e.target.value }))}
          fullWidth
        />
        <AppTextField
          label={t("superAdmin.datasources.adminUsername")}
          value={form.adminUsername}
          onChange={(e) => setForm((f) => ({ ...f, adminUsername: e.target.value }))}
          fullWidth
        />
        <AppTextField
          label={
            passwordConfigured
              ? t("superAdmin.datasources.adminPasswordKeep")
              : t("superAdmin.datasources.adminPassword")
          }
          type="password"
          value={form.adminPassword}
          onChange={(e) => setForm((f) => ({ ...f, adminPassword: e.target.value }))}
          fullWidth
          autoComplete="new-password"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.enabled}
              onChange={(_, checked) => setForm((f) => ({ ...f, enabled: checked }))}
            />
          }
          label={t("superAdmin.datasources.enabled")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.sslInsecure}
              onChange={(_, checked) => setForm((f) => ({ ...f, sslInsecure: checked }))}
            />
          }
          label={t("superAdmin.datasources.sslInsecure")}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.filterAdByBackendRoles}
              onChange={(_, checked) =>
                setForm((f) => ({ ...f, filterAdByBackendRoles: checked }))
              }
            />
          }
          label={t("superAdmin.datasources.filterAdByBackendRoles")}
        />
        <Typography variant="body2" color="text.secondary">
          {t("superAdmin.datasources.hint")}
        </Typography>
        <Stack direction="row" spacing={1}>
          <AppButton variant="outlined" onClick={() => void onTest()} disabled={testing || saving}>
            {testing ? t("superAdmin.datasources.testing") : t("superAdmin.datasources.test")}
          </AppButton>
          <AppButton onClick={() => void onSave()} disabled={saving || testing}>
            {saving ? t("common.saving") : t("common.save")}
          </AppButton>
        </Stack>
      </Stack>
    </PageSectionCard>
  );
}
